const NeutronAsAnEcosystem = () => {
  return (
    <div
      style={{
        maxWidth: 'calc(max(min(100vh, 100vw) - 65px, 500px))',
        fontSize: '18px',
      }}
    >
      <h1>The Neutron as an Ecosystem</h1>
      <p style={{borderLeft: '4px solid #999', paddingLeft: 20}}>
        <strong>Note:</strong> This is a play pretend essay. It's speculative.
        I'm writing about neutrons as I imagine them to be, rather than as they
        actually are.
      </p>
      <p>
        Please imagine a single neutron, perhaps it was just flung from the
        decaying uranium under Pripyat, within the next hour or so it will have
        decayed into a proton. Our best theories can’t say exactly why this will
        happen, but we know it will. Depending upon what kind of physicist you
        are, what associations you have in your mind, you may be picturing any
        of several possible representations: billiard-ball-looking quarks
        passing smaller billiard-ball gluons between them, interacting potential
        basins, a Feynman diagram, some other representation, or a combination
        of them. Here’s what I imagine: the neutron is messy and alive, not
        alive like an individual, but alive like an ecosystem, and it’s immense
        in scale.
      </p>
      <div style={{display: 'flex', justifyContent: 'center'}}>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{height: 192, padding: 10, border: '1px solid #ccc'}}
            alt="beta decay potential"
            src="/neutron/beta-decay-potential.png"
          />
          <p>
            Potential Basins
            <br />(
            <a href="https://www.desmos.com/calculator/enbfcs3xtv">
              desmos.com/calculator/enbfcs3xtv
            </a>
            )
          </p>
        </span>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{padding: 10, height: 192, border: '1px solid #ccc'}}
            alt="beta decay billiard"
            src="/neutron/beta-decay-billiard.png"
          />
          <p>Billiard Balls</p>
        </span>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{padding: 10, height: 192, border: '1px solid #ccc'}}
            alt="beta decay feynmann"
            src="/neutron/beta-decay-feynmann.png"
          />
          <p>Feynmann Diagram</p>
        </span>
      </div>
      <p>
        Three rotating globes (valence quarks) float about me, they are not
        rigid and their boundaries are not definite but fuzzy like the surface
        of Jupiter, they rest on their sides such that their poles are oriented
        towards the arrangement’s centre, they are not perfectly round but
        subtly bulge in places like a peeled tangerine, their shape isn’t static
        but slowly wobbles and ripples like a sheet of flexible plastic going
        wubwubwub. I put on my gamma ray goggles and am suddenly overwhelmed by
        a flurry of activity, the three languid globes are now almost obscured
        by a great multitude of gluons and sea quarks, some tiny, some quite
        large, and every size between, they dance like Mandelbrot starlings,
        following a pattern I feel vaguely aware of but can’t quite comprehend.
        They do not float freely, but are like beads on a string; there are long
        strands, quite thin, that connect the valence quarks (flux tubes), they
        have frayed and broken in many places, and where they are broken the
        gluons and sea quarks, the beads, hold the severed ends together.
      </p>
      <div style={{textAlign: 'center'}}>
        <img
          style={{
            maxWidth: '100%',
            maxHeight: '190px',
            padding: 10,
            border: '1px solid #ccc',
          }}
          alt="neutron proton top cutaway"
          src="/neutron/neutron_proton_top_cutaway.png"
        />
        <p style={{width: 450, margin: '0 auto'}}>
          Neutron (left) and proton (right), as 2D cross-sections. Showing
          distribution and orientation of the flux tubes and valence quarks.
        </p>
      </div>
      <p>
        I focus my attention to the end of one flux tube, where it connects with
        a valence, and watch it for some zeptominutes. It seems to gnaw at the
        valence, pulling energy into itself and growing, flexing and pushing the
        neutron apart as a result. Not all of this energy goes into the tube’s
        elongation, some is directly funnelled into the valence at the other
        end. As the flux tube lengthens it seems to tangle more easily, and
        where it tangles I see gluons emerge from within the ensuing turbulence,
        as if taking opportunity at the flux tubes’ momentary instability. These
        events, that I call gluon snaps, leave the flux tube a little shorter
        than it was before the snap, pulling the neutron together. The gluons,
        in this fish-eat-fish world, don’t stand much of a chance and are
        short-lived, they either: break apart into sea quarks, get absorbed by
        the flux tube as it restiches itself, or get absorbed by one of the
        large valence quarks. Each element of the neutron seems independent yet
        intertwined, caught up in their own survival and growth, formed and
        consumed in an endless loop such that their energy is continuously
        recycled through the system.
      </p>
      <p>
        The neutron functions by the same principle as an ecology, where each
        animal acts in its own interest yet despite a lack of central
        coordination their populations maintain balance with each other through
        the nutrient cycle, creating an endless circle of life. In the neutron,
        competition between its various elements prevents any single element, or
        the neutron as a whole, from endlessly growing in size; the competition
        creates a collection of interlocking constraints that form and maintain
        the neutron’s balance and structure.
      </p>
      <div style={{textAlign: 'center'}}>
        <img
          style={{
            maxWidth: '100%',
            maxHeight: '400px',
            padding: 10,
            border: '1px solid #ccc',
          }}
          alt="rough 2"
          src="/neutron/rough_2.png"
        />
        <p style={{width: 640, margin: '0 auto'}}>
          Deuterium nucleus in 3D, contains one neutron (bottom) and one proton
          (top). Showing spin alignment. References (with some imaginative
          interpretation):
          <br />
          The deuteron: structure and form factors
          (https://core.ac.uk/download/pdf/25306971.pdf)
          <br />
          http://www.unclear2nuclear.com/deuteron.php
          <br />
          https://backreaction.blogspot.com/2017/12/get-your-protons-right.html
        </p>
      </div>
      <h2>Uncategorised Notes</h2>
      <p>
        Most people think of dimensions as a thing that gets added; as in, one
        may start with 2 dimensions, add a dimension and then have 3 dimensions.
        But this gets us into trouble when we start asking “where do dimensions
        come from?”, especially in the context of those esoteric physics that go
        like “oh, there's 29 dimensions” and another contradicts it like “no!
        there are clearly 11!”. I mean, where do these numbers come from? It
        feels like they're plucked from the aether for the sake of mathematical
        convenience and have no causal explanation.
      </p>
      <p>
        I suggest we reorient from thinking in degrees of freedom to thinking in
        degrees of constraint/invariance. So with zero degrees of constraint we
        have infinite dimensions like in basic quantum mechanics (generalised
        Hamiltonian coordinates), and this ends-up being the simplest starting
        point if we're to look for an emergent reason for physics being they way
        they are. For the intuition, when a bubble pops it goes from a 2D
        surface to 3D vapour, because the surface tension constraint was
        removed; when a driver falls asleep at the wheel, the car goes from
        travelling along a 1D path, to travelling across a 2D plane, because the
        steering constraint was removed. Removing constraints adds dimensions
        and vice-versa. The question then becomes “where do constraints emerge
        from?”; and, unlike for dimensions, there's a whole bunch of grounded
        research in meteorology and theoretical ecology to answer that.
      </p>
      <p>
        We can also gain benefit by reorienting from thinking of dimensions as
        sets of orthogonal coordinates to thinking in orthogonal directions of
        travel. Specifically, thinking in directions reorients us from thinking
        about structural correlations to causal structure, pulling us away from
        mere descriptions of reality and closer to the whys and explanations of
        reality. With this reorientation, points become -1D (minus one
        dimensional), rays become 0D, and we use rays to construct higher
        dimensions like so:
      </p>
      <div style={{textAlign: 'center'}}>
        <img
          style={{
            maxWidth: '100%',
            maxHeight: '400px',
            padding: 10,
            border: '1px solid #ccc',
          }}
          alt="hypersphere outline"
          src="/neutron/hypersphere_outline.png"
        />
        <p style={{width: 510, margin: '0 auto'}}>
          Dimensions. The rays extend out to the surface of an
          infinite-hypersphere with infinite radius, which we've projected into
          3D space for illustrative purposes.
        </p>
      </div>

      {/* <h2>Rough Notes</h2>
      <p>TODO</p>
      <h3>Baryon Genesis and Structure</h3>
      <p>It's my hunch th</p>
      <h3>Curl-Mass Equilvalence</h3>
      <ul>
        <li>Charm, Strange and Muon: golf balls</li>
        <li>Top, Bottom and Tau: brain convolutions</li>
        <li>
          <a
            rel="noopener"
            target="_blank"
            href="https://news.mit.edu/2015/predicting-wrinkles-fingerprints-curved-surfaces-0202"
          >
            Wrinkle Predictions
          </a>{' '}
          (MIT)
        </li>
        <li>
          Photon:{' '}
          <a
            rel="noopener"
            target="_blank"
            href="https://phys.org/news/2012-04-mathematics-first-ever-image-flat-torus.html"
          >
            flat torus
          </a>
        </li>
      </ul>
      <h3>Section</h3>
      <p>
        Predominantly <Tex>{`qq`}</Tex> rather than the usual{' '}
        <Tex>{`q\\overline{q}`}</Tex>, which then decay into neutrons,
      </p>
      <h3>Some Drawings</h3>
      <div style={{display: 'flex', justifyContent: 'center'}}>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{padding: 10, height: 192, border: '1px solid #ccc'}}
            alt="rough 1"
            src="/neutron/rough_1.png"
          />
          <p>Proton and neutron</p>
        </span>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{padding: 10, height: 192, border: '1px solid #ccc'}}
            alt="rough 2"
            src="/neutron/rough_2.png"
          />
          <p>Deuterium</p>
        </span>
      </div>
      <p>Work-in-progress</p>
      <div style={{display: 'flex', justifyContent: 'center'}}>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{padding: 10, height: 150, border: '1px solid #ccc'}}
            alt="rough 3"
            src="/neutron/rough_3.png"
          />
          <p>Vortex Ring. Has both negative and positive curl</p>
        </span>
      </div>
      <p>Work-in-progress</p>
      <div style={{display: 'flex', justifyContent: 'center'}}>
        <span style={{padding: 10, textAlign: 'center'}}>
          <img
            style={{padding: 10, height: 192, border: '1px solid #ccc'}}
            alt="rough 4"
            src="/neutron/rough_4.png"
          />
          <p>
            Soft Gears (metaphor for spin). Proton-proton is very locally
            stable,
            <br />
            and causes global instability (global = affecting whole nucleus)
          </p>
        </span>
      </div> */}
    </div>
  )
}

export default NeutronAsAnEcosystem
